import React from 'react';
import styled from 'styled-components';
import { useFreeListens } from '../../contexts/FreeListenContext';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const Title = styled.h2`
  color: white;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 16px 0;
  line-height: 1.3;
`;

const Message = styled.p`
  color: rgba(255, 255, 255, 0.95);
  font-size: 18px;
  margin: 0 0 12px 0;
  line-height: 1.6;
`;

const Count = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 16px;
  margin: 0 0 32px 0;
  font-style: italic;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;

  ${props => props.variant === 'primary' ? `
    background-color: white;
    color: #667eea;

    &:hover {
      background-color: #f0f0f0;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    }
  ` : `
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    border: 2px solid white;

    &:hover {
      background-color: rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
    }
  `}

  &:active {
    transform: translateY(0);
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px 0 32px 0;
  text-align: left;
`;

const BenefitItem = styled.li`
  color: rgba(255, 255, 255, 0.9);
  font-size: 15px;
  margin-bottom: 12px;
  padding-left: 28px;
  position: relative;
  line-height: 1.5;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    font-weight: bold;
    font-size: 18px;
    color: #90EE90;
  }
`;

interface FreemiumGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignIn: () => void;
  onSignUp: () => void;
}

export function FreemiumGateModal({ isOpen, onClose, onSignIn, onSignUp }: FreemiumGateModalProps) {
  const { freeListenCount } = useFreeListens();

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Title>Sign up to listen for free</Title>
        <Count>You've listened to {freeListenCount} free meditation{freeListenCount !== 1 ? 's' : ''}</Count>

        <Message>Create a free account to unlock:</Message>

        <BenefitsList>
          <BenefitItem>Additional meditation listens</BenefitItem>
          <BenefitItem>Save your favorites</BenefitItem>
          <BenefitItem>Follow your favorite creators</BenefitItem>
        </BenefitsList>

        <Message>Subscribe to unlock:</Message>

        <BenefitsList>
          <BenefitItem>Unlimited meditation listens</BenefitItem>
          <BenefitItem>Create your own meditations</BenefitItem>
          <BenefitItem>Customize meditations</BenefitItem>
          <BenefitItem>Private meditations & more</BenefitItem>
        </BenefitsList>

        <ButtonContainer>
          <Button variant="primary" onClick={onSignUp}>
            Sign Up Free
          </Button>
          <Button variant="secondary" onClick={onSignIn}>
            Sign In
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
}
